import { useMemo } from "react";
import { useAppSelector } from "@/store";
import { useGetTenantAllowedPagesQuery } from "@/graphql/page-access";
import { hasPageAccess, hasFullSectionAccess } from "@/roles/protection/pageAccess";
import { roles } from "@/roles";

interface Tab {
  text: string;
  link: string;
}

export const useFilteredTabs = (
  allTabs: Tab[],
  tabKeyMap: Record<string, string>,
  parentKey: string
): Tab[] => {
  const { user } = useAppSelector((state) => state.auth);
  const role = (user as any)?.userRole;
  const shouldFetch = [roles.SPOWNER, roles.SPPARTNER, roles.SPCLIENT, roles.TDCLIENT].includes(role);
  const { data: tenantPagesResp } = useGetTenantAllowedPagesQuery(undefined, { skip: !shouldFetch });

  return useMemo(() => {
    if (role === roles.TDADMIN) return allTabs;

    const userPages = (user as any)?.allowedPages as string[] | undefined;
    const pages = userPages ?? tenantPagesResp?.pages;

    // Pre-hydration or no pages configured: show all
    if (!pages) return allTabs;

    // Parent key or legacy equivalent grants all tabs (tab-level keys do not)
    if (hasFullSectionAccess(pages, parentKey)) return allTabs;

    return allTabs.filter((tab) => {
      const requiredKey = tabKeyMap[tab.link];
      if (!requiredKey) return true;
      if (requiredKey === "__ALL__") {
        // Special: visible only when all other tab keys in this group are accessible
        return Object.entries(tabKeyMap)
          .filter(([, v]) => v !== "__ALL__")
          .every(([, key]) => hasPageAccess(pages, key));
      }
      return hasPageAccess(pages, requiredKey);
    });
  }, [user, tenantPagesResp, allTabs, tabKeyMap, parentKey, role]);
};
